import type { ComunicacaoProcessualItem, PrazoLegalPresumido } from '@/api/client';
import { OQueHaParaFazer } from '@/components/ui/DrawerDetalhe';
import { formatDate } from '@/lib/formatters';
import {
  diasDoPrazo,
  ehPresumido,
  providenciaDoPrazo,
  prazosSecundarios,
  ressalvaDaLeitura,
  trechoDoAto,
} from '@/lib/prazos/presumido';
import { rotuloDoVencimento } from '@/lib/prazos/vencimento';
import { ContaDoPrazo } from './ContaDoPrazo';

interface PrazoDaLeiProps {
  comunicacao: ComunicacaoProcessualItem;
  podeLancar: boolean;
}

/**
 * "O que há para fazer" quando a intimação não diz o prazo (`prazo_estado = 'presumido'`):
 * como no prazo do texto, a citação do trecho que o gerou — aqui, a frase do ato
 * ("JULGO IMPROCEDENTE…"), destacada também no texto — e, abaixo, o prazo que a lei dá a
 * esse ato, com o dispositivo e o vencimento.
 *
 * Com o vencimento do principal (`prazo_calculo.origem = 'presumido'`), ele vai na caixa
 * e a conta abaixo; os embargos de declaração e os demais ficam como informação, e não
 * vão à agenda. Sem esse vencimento (data de outra origem, conta que não deu), todos os
 * prazos da lei aparecem como informação — nunca silêncio.
 */
export function PrazoDaLei({ comunicacao, podeLancar }: PrazoDaLeiProps) {
  const dados = comunicacao.prazo_presumido;
  const calculo = ehPresumido(comunicacao.prazo_calculo) ? comunicacao.prazo_calculo! : null;
  const estimado = calculo?.estimado ?? dados?.estimado ?? false;
  const outros = calculo ? prazosSecundarios(comunicacao) : (dados?.prazos ?? []);
  // O aviso penal já vai na ressalva (aviso_regime): a lista não o repete.
  const ressalvas = (dados?.ressalvas ?? []).filter((r) => r !== calculo?.aviso_regime);
  const citacao = trechoDoAto(comunicacao.texto, dados?.trecho);
  const frase = citacao ? `“${citacao}”` : (dados?.ato_rotulo ?? 'Ato sem prazo no texto');

  return (
    <>
      <OQueHaParaFazer
        frase={frase}
        origem={
          calculo
            ? `${providenciaDoPrazo(calculo)}: ${diasDoPrazo(calculo.dias, calculo.regime)}${calculo.em_dobro ? ', em dobro' : ''} (${calculo.dispositivo}). ${rotuloDoVencimento(calculo.vencimento, calculo.estimado).longo}.`
            : undefined
        }
        // Curta: o texto da intimação, logo abaixo, é o objeto da tela. O resto (estimado,
        // aviso penal, demais prazos) fica dentro da conta, fechada.
        ressalva={
          (calculo && ressalvaDaLeitura(calculo)) ??
          'A intimação não diz o prazo: é o da lei para este ato — confira no tribunal.'
        }
      />
      {calculo ? (
        <ContaDoPrazo
          calculo={calculo}
          podeLancar={podeLancar}
          rotulo={outros.length > 0 ? 'Ver a conta e os outros prazos da lei' : 'Ver a conta'}
        >
          <Detalhes
            avisos={[calculo.estimado ? calculo.motivo_estimado : null, calculo.aviso_regime, ...ressalvas]}
            outros={outros}
            estimado={estimado}
            titulo="A lei também prevê:"
          />
        </ContaDoPrazo>
      ) : (
        <Detalhes avisos={ressalvas} outros={outros} estimado={estimado} titulo="A lei prevê:" />
      )}
    </>
  );
}

function Detalhes({
  avisos,
  outros,
  estimado,
  titulo,
}: {
  avisos: (string | null | undefined)[];
  outros: PrazoLegalPresumido[];
  estimado: boolean;
  titulo: string;
}) {
  const textos = avisos.filter((a): a is string => !!a);
  return (
    <>
      {outros.length > 0 && (
        <div className="mt-3">
          <p className="mb-1.5 text-xs text-muted-foreground">{titulo}</p>
          <ul className="space-y-1 text-sm">
            {outros.map((p) => (
              <LinhaDoPrazo key={p.providencia} prazo={p} estimado={estimado} />
            ))}
          </ul>
        </div>
      )}
      {textos.length > 0 && (
        <ul className="mt-3 space-y-1 text-xs leading-relaxed text-muted-foreground">
          {textos.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      )}
    </>
  );
}

function LinhaDoPrazo({ prazo, estimado }: { prazo: PrazoLegalPresumido; estimado: boolean }) {
  return (
    <li className="break-words">
      <span className="font-medium">{providenciaDoPrazo(prazo)}</span>
      {' — '}
      {diasDoPrazo(prazo.dias, prazo.regime)}
      {/* Do mesmo lugar da conta: estimada lá, estimada aqui. */}
      {prazo.vencimento && `, até ${estimado ? '~' : ''}${formatDate(prazo.vencimento)}${estimado ? ' (estimado)' : ''}`}
      <span className="text-muted-foreground"> ({prazo.dispositivo})</span>
    </li>
  );
}
